import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { IAddress } from '@r1-frontend/shared/types/IAddress';

import { getFilterStreets } from '~/src/helpers/getFilterStreets';
import { TState } from '~/src/store';
import { getCurrentCity, selectCitiesOptionWithProvider } from '~/src/store/selectors/city';

import {
    fetchHouseList,
    setData,
    setFlat,
    setHouseList,
    setHouseText,
    setSelectedCity,
    setSelectedHouse,
    setSelectedStreet,
    setStreetText,
} from './store/actions';
import { addressFieldsReducer, getInitialState } from './store/reducer';
import { IProps } from './types';

import * as ST from './styled';

const MAX_FLAT_LENGTH = 6;

const OPTION_KEYS = {
    valueKey: 'domain',
    labelKey: 'name',
};

const setFocusByRef = (elementRef: React.RefObject<HTMLInputElement>) => {
    if (elementRef.current) {
        elementRef.current.focus();
    }
};

const dropdownProps = {
    asyncMode: true,
    cacheOptions: false,
    isSearchable: true,
    isClearable: true,
    components: {
        DropdownIndicator: null,
    },
    loadingMessage: () => 'Поиск',
};

// формирование сообщения, если нет options
const noOptionsMessage = ({ inputValue } : { inputValue: string }) => inputValue === '' ? 'Введите адрес' : 'Не найдено';
const noOptionsMessageHouse = ({ inputValue } : { inputValue: string }) => inputValue === '' ? 'Укажите номер дома' : 'Не найдено';

/**
 * Компонент ввода адреса
 * catchAddressData - cb для обработки введенного адреса
 * onClickCityChange - cb если передан, селект выбора города работает как кнопка
 */
export const AddressInputFields = ({
    catchAddressData,
    disableCityChange,
    isDisableFields = false,
    defaultAddress,
    withSatelliteFilter,
    gtmSenderCallback,
    onClickCityChange,
    ...rest
}: IProps): JSX.Element => {
    const isSetFocus = useRef(false);
    const flatInput = useRef<HTMLInputElement>(null);
    const streetDropDown =  useRef<HTMLInputElement>(null);
    const houseDropDown =  useRef<HTMLInputElement>(null);
    const [isFlatFillStarted, setIsFlatFillStarted] = useState(false);

    const { citiesOptions, currentCity, providerId } = useSelector((state: TState) => ({
        citiesOptions: selectCitiesOptionWithProvider({ state, optionKeys: OPTION_KEYS }),
        currentCity: getCurrentCity(state),
        providerId: state.city.provider.providerId,
    }));

    const initCity = { value: { domain: currentCity.domain, providerId }, label: currentCity.name };

    const [state, dispatch] = useReducer(addressFieldsReducer, getInitialState({
        initCity: initCity,
        initStreet: null,
        initHouse: null,
        initFlat: '',
    }));

    const {
        selectedCity,
        streetText,
        selectedStreet,
        houseText,
        selectedHouse,
        flat,
        houseList,
    } = state;

    const address = useMemo(() => ({
        city: selectedCity,
        street: selectedStreet,
        house: selectedHouse,
        flat,
    }), [selectedCity, selectedStreet, selectedHouse, flat]);

    const getHouseList = useCallback(async(streetId) => {
        const domain = selectedCity?.value?.domain;

        if (streetId && domain) {
            const houses = await fetchHouseList(streetId);
            dispatch(setHouseList(houses));
        }
    }, [selectedCity?.value?.domain]);

    useEffect(() => {
        const {
            street: initStreet,
            house: initHouse,
            flat: initFlat,
        } = defaultAddress || {};

        dispatch(setData({
            selectedStreet: initStreet || null,
            selectedHouse: initHouse || null,
            flat: initFlat || '',
        }));

        const streetId = initStreet?.value;
        if (streetId) {
            getHouseList(streetId);
        }
    }, [defaultAddress, getHouseList]);

    const sendAddressData = useCallback((addressValue: Partial<IAddress>) => {
        catchAddressData(addressValue);
    }, [catchAddressData]);

    const onChangeCity = useCallback((option) => {
        if (option) {
            dispatch(setSelectedCity(option));
        }
    }, []);

    const onChangeStreet = useCallback((searchText) => {
        dispatch(setStreetText(searchText));
        isSetFocus.current = true;
    }, []);

    const onChangeHouse = useCallback((searchText) => {
        dispatch(setHouseText(searchText));
        isSetFocus.current = true;
    }, []);

    const onSelectStreet = useCallback(async(option) => {
        dispatch(setSelectedStreet(option));

        sendAddressData({
            ...address,
            street: option,
        });

        const streetId = option?.value;
        getHouseList(streetId);
        if (streetId) {
            gtmSenderCallback('address verification street');
        }
    }, [sendAddressData, address, getHouseList]);

    const onHouseSelect = useCallback((option) => {
        dispatch(setSelectedHouse(option));

        sendAddressData({
            ...address,
            house: option,
        });

        if (option?.value) {
            gtmSenderCallback('address verification house');
        }
    }, [address, sendAddressData, gtmSenderCallback]);

    /* Произошла смена улицы, фокус на ввод дома */
    useEffect(() => {
        if (selectedStreet?.value && isSetFocus.current) {
            setFocusByRef(houseDropDown);
        }
    }, [selectedStreet]);

    /* Произошла смена дома, фокус на ввод квартиры */
    useEffect(() => {
        if (selectedHouse?.value && isSetFocus.current) {
            setFocusByRef(flatInput);
        }
    }, [selectedHouse]);

    const loadStreetsOptions = useCallback(async(searchText) => {
        if (!selectedCity) {
            return;
        }

        const resp = await getFilterStreets({ term: searchText, withSatelliteFilter }, selectedCity.value.providerId);
        return resp?.map(el => ({ value: el.street_id, label: el.street_name }));
    }, [selectedCity, withSatelliteFilter]);

    const loadHouseOptions = useCallback(async(searchText) => {
        return houseList.filter(el => el.label.includes(searchText) && new RegExp(`^${searchText}`, 'g').test(el.label));
    }, [houseList]);

    const onChangeFlat = useCallback(async(event) => {
        const value = event.target.value;
        if (value?.length <= MAX_FLAT_LENGTH) {
            if (!isFlatFillStarted) {
                gtmSenderCallback('address verification apartment number');
                setIsFlatFillStarted(true);
            }
            dispatch(setFlat(value || ''));
            sendAddressData({
                ...address,
                flat: value || '',
            });
        }
    }, [address, sendAddressData, gtmSenderCallback, isFlatFillStarted]);

    return (
        <ST.AddressInputFields {...rest} className={'ym-hide-content'}>
            <ST.Btn isActive={!!onClickCityChange} onClick={onClickCityChange}>
                <ST.DropDown
                    {...dropdownProps}
                    placeholder='Город'
                    // @ts-ignore
                    defaultValue={selectedCity}
                    asyncMode={false}
                    options={citiesOptions || []}
                    onChange={onChangeCity}
                    components={{
                        DropdownIndicator: ST.DropdownIndicator,
                    }}
                    isDisabled={disableCityChange}
                    openMenuOnClick={!onClickCityChange}
                    isClearable={!onClickCityChange}
                />
            </ST.Btn>
            <ST.DropDown
                {...dropdownProps}
                placeholder='Улица'
                // @ts-ignore
                defaultValue={selectedStreet}
                loadOptions={loadStreetsOptions}
                value={selectedStreet}
                onChange={onSelectStreet}
                inputValue={streetText}
                onInputChange={onChangeStreet}
                noOptionsMessage={noOptionsMessage}
                selectRef={streetDropDown}
                isDisabled={isDisableFields || !selectedCity?.value}
                dataTest='select-street'
            />
            <ST.DropDown
                {...dropdownProps}
                placeholder='Дом'
                // @ts-ignore
                defaultValue={selectedHouse}
                loadOptions={loadHouseOptions}
                value={selectedHouse}
                onChange={onHouseSelect}
                inputValue={houseText}
                onInputChange={onChangeHouse}
                noOptionsMessage={noOptionsMessageHouse}
                selectRef={houseDropDown}
                isDisabled={isDisableFields || !selectedStreet?.value}
                dataTest='select-home'
            />
            <ST.InputElement
                ref={flatInput}
                value={flat}
                onChange={onChangeFlat}
                placeholder='Квартира'
                disabled={isDisableFields || !selectedHouse?.value}
                inputType={'integer'}
                data-test='select-flat'
            />
        </ST.AddressInputFields>
    );
};

export default AddressInputFields;
